import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import { EmailAccountCreate, EmailAccountUpdate } from "@/app/types";
import { useCompany } from "./useCompany";

export const emailAccountKeys = {
  all: ["email-accounts"] as const,
  list: (page: number, size: number) =>
    [...emailAccountKeys.all, "list", { page, size }] as const,
  managed: (page: number, size: number) =>
    [...emailAccountKeys.all, "managed", { page, size }] as const,
} as const;

export function useEmailAccounts(page = 1, size = 100) {
  return useQuery({
    queryKey: emailAccountKeys.list(page, size),
    queryFn: () => api.emailAccounts.getEmailAccounts({ page, size }),
  });
}

/**
 * Email accounts the current user can administer (company owner → all company
 * accounts; group super-admin → their groups'). Each row carries the owning
 * user + group name for the admin management surface.
 */
export function useManagedEmailAccounts(
  page = 1,
  size = 100,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: emailAccountKeys.managed(page, size),
    queryFn: () => api.emailAccounts.getManagedEmailAccounts({ page, size }),
    ...options,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────
// Each mutation owns its cache invalidation (in onSuccess / onSettled) so
// components never call `invalidateQueries` directly — refetching after a write
// is query-layer concern, not component logic.

function useInvalidateAll() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: emailAccountKeys.all });
}

export function useCreateEmailAccount() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (account: EmailAccountCreate) =>
      api.emailAccounts.createEmailAccount(account),
    onSuccess: invalidate,
  });
}

export function useUpdateEmailAccount() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (vars: { id: string; account: EmailAccountUpdate }) =>
      api.emailAccounts.updateEmailAccount(vars.id, vars.account),
    onSuccess: invalidate,
  });
}

export function useDeleteEmailAccount() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api.emailAccounts.deleteEmailAccount(id),
    onSuccess: invalidate,
  });
}

export function useTestEmailConnection() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api.emailAccounts.testConnection(id),
    // onSettled: a failed IMAP check still updates connection_status, so refetch
    // either way to surface the new state.
    onSettled: invalidate,
  });
}

export function useForwardingInfo(options?: { enabled?: boolean }) {
  // Scope the cache key to the current company: the preview (domain + tenant
  // slug) is tenant-specific, so a company switch within the same SPA session
  // must not reuse another tenant's slug — otherwise the preview could disagree
  // with the address the backend provisions for the active company.
  const { data: company } = useCompany();
  const companyId = company?.id;
  return useQuery({
    queryKey: [...emailAccountKeys.all, "forwarding-info", companyId] as const,
    queryFn: () => api.emailAccounts.getForwardingInfo(),
    // Stable per company (only changes if the company is renamed); refetch is
    // cheap and the company-scoped key already isolates tenants.
    staleTime: 5 * 60 * 1000,
    enabled: (options?.enabled ?? true) && !!companyId,
  });
}

export function useSetupForwarding() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (vars: { id: string; slug?: string }) =>
      api.emailAccounts.setupForwarding(vars.id, vars.slug),
    onSuccess: invalidate,
  });
}
